<template>
  <component
    :is="as"
    v-bind="$attrs"
    class="ui-text"
    :class="classes"
    :style="color ? { color: `var(--${color})` } : undefined"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
  import type { ColorVariables } from '@shared/types'
  import { computed } from 'vue'

  defineOptions({ inheritAttrs: false, name: 'UiText' })

  export type TextBreakpoint = 'lg' | 'md' | 'sm' | 'xl'
  export type TextSize =
    | 'display-2xl'
    | 'display-lg'
    | 'display-md'
    | 'display-sm'
    | 'display-xl'
    | 'display-xs'
    | 'text-lg'
    | 'text-md'
    | 'text-sm'
    | 'text-xl'
    | 'text-xs'
  export type TextWeight =
    | 'black'
    | 'bold'
    | 'extrabold'
    | 'extralight'
    | 'light'
    | 'medium'
    | 'regular'
    | 'semibold'
    | 'thin'
  export type ResponsiveTextProp<T> = Partial<Record<TextBreakpoint, T>> | T

  const props = withDefaults(
    defineProps<{
      as?: 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'
      color?: ColorVariables
      size?: ResponsiveTextProp<TextSize>
      weight?: ResponsiveTextProp<TextWeight>
    }>(),
    {
      as: 'span',
    },
  )

  const resolvedSize = computed(() => props.size ?? 'text-md')
  const resolvedWeight = computed(() => props.weight ?? 'regular')
  const responsiveClasses = <T extends string>(prefix: string, value: ResponsiveTextProp<T>) =>
    typeof value === 'string'
      ? [`${prefix}-${value}`]
      : Object.entries(value).map(([breakpoint, entry]) => `${breakpoint}:${prefix}-${entry}`)

  const classes = computed(() => [
    ...responsiveClasses('size', resolvedSize.value),
    ...responsiveClasses('weight', resolvedWeight.value),
  ])
</script>

<style scoped lang="scss">
@use '@repo/ui/typography.scss' as typography;


.ui-text {
  margin: 0;


}


@each $size in display-2xl, display-xl, display-lg, display-md, display-sm, display-xs, text-xl,
  text-lg, text-md, text-sm, text-xs {

  .size-#{$size} {
    @include typography.font-size($size);


}


}


@each $weight in thin, extralight, light, regular, medium, semibold, bold, extrabold, black {

  .weight-#{$weight} {
    @include typography.font-weight($weight);


}


}


$breakpoints: (
  sm: 640px,
  md: 768px,
  lg: 1024px,
  xl: 1280px,
);


@each $breakpoint, $width in $breakpoints {
  @media (min-width: $width) {
    @each $size in display-2xl, display-xl, display-lg, display-md, display-sm, display-xs, text-xl,
      text-lg, text-md, text-sm, text-xs {

      .#{$breakpoint}\:size-#{$size} {
        @include typography.font-size($size);


}


}


    @each $weight in thin, extralight, light, regular, medium, semibold, bold, extrabold, black {

      .#{$breakpoint}\:weight-#{$weight} {
        @include typography.font-weight($weight);


}


}


}


}

</style>
